import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AreaModel } from '../area.model';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiTimeZonesService, IFiTimeZone } from '@fi-shared/fi-shared-services/fi-time-zones.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { AppTextService } from '@fi-shared/app-text';
import { ICountryModel } from '@fi-shared/fi-contacts/fi-countries-select/country';
import { AvailableDateFormats, AvailableDateTimeLocale, IAvailableDateTimeFormat } from '@fi-shared/utils';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-area-view',
    templateUrl: './area-view.component.html',
    styleUrls: ['./area-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-area-view' },
    standalone: false,
})
export class AreaViewComponent implements OnInit {
    @Input('area') Area: AreaModel;
    @Input() countries: ICountryModel[];

    HasEditAccess: boolean = false;
    EditTabs: EditTabsModel;
    MoreMenuItems: MoreMenuMobileSettingsModel;

    constructor(
        private stateAccessCheckService: StateAccessCheckService,
        private statisticService: StatisticService,
        private timeZonesService: FiTimeZonesService,
        private stateService: FiStateService,
        private detectDevicesService: DetectDevicesService,
        private fiFormatDateService: FiFormatDateService,
        private appTextService: AppTextService,
    ) {}

    ngOnInit() {
        this.HasEditAccess = this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.Area.Edit);
        this.setPageHeaderData();
        this.statisticService.Visit(ModuleType.Core, ObjectTypes.Area, this.Area.Id);
    }

    private setPageHeaderData() {
        this.EditTabs = new EditTabsModel(
            `${this.appTextService.get('common.area')}: ${this.Area.Name}`,
            this.appTextService.get('back'),
        );
        this.MoreMenuItems = new MoreMenuMobileSettingsModel();

        if (this.HasEditAccess) {
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.edit'), () => this.editArea());
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.delete'), () => this.deleteArea());
        }
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    getTimeZoneDescription(): string {
        const timeZones: IFiTimeZone[] = this.timeZonesService.getTimeZones();
        const timeZone: IFiTimeZone = timeZones.find((item) => item.name === this.Area.TimeZoneName);
        return timeZone ? timeZone.description : this.Area.TimeZoneName;
    }

    editArea() {
        this.stateService.go(FiStateNames.Area.Edit, { Id: this.Area.Id });
    }

    deleteArea() {
        this.stateService.go(FiStateNames.Area.Delete, { Id: this.Area.Id });
    }

    getPhoneCountryCode() {
        return this.countries.find((country) => country.Id === this.Area.PhoneCountryId)?.CountryCode;
    }

    getPreferredDateFormatLabel(): string {
        return AvailableDateFormats.find((dateFormat: IAvailableDateTimeFormat) => {
            return dateFormat.locale === (this.Area.PreferredDateFormat || AvailableDateTimeLocale.Swedish);
        })?.label;
    }

    getPreferredTimeFormatLabel(): string {
        return this.fiFormatDateService.timeFormas.find((timeFormat: IAvailableDateTimeFormat) => {
            return timeFormat.locale === (this.Area.PreferredTimeFormat || AvailableDateTimeLocale.Swedish);
        })?.label;
    }

    protected readonly FiDisplayType = FiDisplayType;
}
